/**
 * RetakeAndHistoryPanel — sits on /my-matches/:sessionId.
 * Lets the user retake the RV match application (resets the autosave draft
 * and routes them to the homepage form) AND lists their past application
 * attempts so they can switch between them.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, History, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RetakeAndHistoryPanel = ({ email, currentSessionId, isDark }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) {
      setLoading(false);
      return;
    }
    let active = true;
    axios.get(`${API}/quiz/sessions-by-email/${encodeURIComponent(email)}`)
      .then(res => { if (active) setSessions(res.data?.sessions || []); })
      .catch(() => { if (active) setSessions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [email]);

  const confirmRetake = () => {
    // Clear the autosave so the form loads completely fresh
    try { localStorage.removeItem('rvmatch_form_draft_v2'); } catch { /* ignore */ }
    setShowConfirm(false);
    navigate('/');
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  const past = sessions.filter(s => s.session_id !== currentSessionId);

  return (
    <div
      className={`rounded-2xl ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'} p-5 sm:p-6 shadow-sm`}
      data-testid="retake-history-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#F2A900]" />
          <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Retake or Switch Application
          </h3>
        </div>
        <Button
          onClick={() => setShowConfirm(true)}
          className="bg-[#F2A900] hover:bg-[#E89400] text-black font-semibold rounded-full"
          data-testid="retake-application-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Retake the application
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-3">
          <Loader2 className={`w-4 h-4 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading your past applications…</span>
        </div>
      ) : past.length === 0 ? (
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-testid="no-past-attempts">
          This is your first application. New retakes will be saved to your profile here.
        </p>
      ) : (
        <div className="space-y-2" data-testid="past-attempts-list">
          <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Past attempts ({past.length})
          </p>
          {past.map((s) => {
            const rv = s.matching_criteria?.rv_type?.replace(/_/g, ' ') || 'Any RV';
            const budget = s.matching_criteria?.budget_max
              ? `$${Number(s.matching_criteria.budget_max).toLocaleString()}`
              : 'Flexible';
            return (
              <Link
                key={s.session_id}
                to={`/my-matches/${s.session_id}`}
                className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition-colors ${
                  isDark
                    ? 'border-gray-800 bg-gray-800/40 hover:border-gray-600'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-400'
                }`}
                data-testid={`past-attempt-${s.session_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {fmtDate(s.created_at)} — {rv} · {budget}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {s.matching_criteria?.rv_length ? `Length: ${s.matching_criteria.rv_length} · ` : ''}
                    Sleeps {s.matching_criteria?.sleep_capacity || '?'}
                  </p>
                </div>
                <ChevronRight className={`shrink-0 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </Link>
            );
          })}
        </div>
      )}

      {/* Retake confirmation modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowConfirm(false)}
          data-testid="retake-confirm-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${
              isDark ? 'bg-gray-900 border border-gray-700 text-white' : 'bg-white text-gray-900'
            }`}
            data-testid="retake-confirm-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-extrabold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Start a fresh application?
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  This clears any in‑progress answers and takes you back to the homepage to retake the RV match application. Your current matches and past attempts stay saved on your account.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
              <Button
                onClick={() => setShowConfirm(false)}
                variant="outline"
                className={`rounded-full ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : ''}`}
                data-testid="retake-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRetake}
                className="bg-[#F2A900] hover:bg-[#E89400] text-black font-semibold rounded-full"
                data-testid="retake-confirm-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Yes, retake it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetakeAndHistoryPanel;
